import React, { useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { ref, set, runTransaction } from "firebase/database";
import { auth, db } from "../../firebase-config";
import { useAnalysis } from "../../contexts/AnalysisContext.js";
import { Eye, EyeOff } from "lucide-react";

const SignupComponent = ({ onSwitchMode }) => {
  const [style, setStyle] = useState({});
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setLoginTime } = useAnalysis();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Function to get the current formatted time
  const getCurrentFormattedTime = () => {
    const now = new Date();
    return now.toLocaleString(); // e.g., '8/27/2024, 10:30:00 AM'
  };
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth >= 1024) {  // Assuming 1024px is your 'lg' breakpoint
        setStyle({
          backgroundImage: "url('backpic.jpeg')"
        });
      } else {
        setStyle({
          backgroundImage: "linear-gradient(to top, #9be15d 0%, #00e3ae 100%)" // Example gradient
        });
      }
    }
  
    // Set style on initial mount
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Function to generate a unique 15-character string
  const generateUniqueId = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 25; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Function to get the next user ID
  const getNextUserId = async () => {
    const userCountRef = ref(db, "userCount");
    let newUserId;

    await runTransaction(userCountRef, (currentCount) => {
      if (currentCount === null) {
        newUserId = 1;
        return 1;
      } else {
        newUserId = currentCount + 1;
        return currentCount + 1;
      }
    });

    return `user${newUserId}`;
  };

  // Function to save user to the database
  const saveUserToDatabase = async (user) => {
    const userId = await getNextUserId();
    const uniqueUid = generateUniqueId();
  
    // Ensure email is formatted correctly

    let formattedEmail = email.includes("@") ? email : `${email}@crop2x.com`;
    const userRef = ref(db, "users/" + userId);
  
    await set(userRef, {
      email: formattedEmail,
      uid: uniqueUid,
      provider: user.providerData[0].providerId,
    });
  
  };
  

  const submitHandler = async (event) => {
    event.preventDefault();
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
   
    try {
      setLoading(true);
      setError("");
      let formattedEmail = email.includes("@") ? email : `${email}@crop2x.com`;
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formattedEmail,
        password
      );
      localStorage.setItem("User",formattedEmail)
      await saveUserToDatabase(userCredential.user);
      // date
      const formattedTime = getCurrentFormattedTime();
      setLoginTime(formattedTime);

    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const googleSignUpHandler = async () => {
    try {
      setLoading(true);
      setError("");
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      await saveUserToDatabase(userCredential.user);
      // date
      const formattedTime = getCurrentFormattedTime();
      setLoginTime(formattedTime);

    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const facebookSignUpHandler = async () => {
    try {
      setLoading(true);
      setError("");
      const provider = new FacebookAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      await saveUserToDatabase(userCredential.user);
      // date
      const formattedTime = getCurrentFormattedTime();
      setLoginTime(formattedTime);

    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div
      className={`w-full min-h-screen flex flex-col justify-between bg-cover bg-center px-4 sm:px-10 md:px-12 lg:px-14 2xl:px-24 transition-all pb-8 md:pb-10 xl:pb-12 text-white`}
       style={style} 
    >

      <div className="h-[50px] 2xl:h-[60px] flex items-end">
        <h2 className="text-lg md:text-xl 2xl:text-3xl font-medium md:font-semibold">
          CROP2X
        </h2>
      </div>

      <div className="flex-1 flex items-center justify-center lg:justify-end">
        <div
          className="w-full md:w-[70%] lg:w-[400px] xl:w-[500px] 2xl:w-[600px] px-4 md:px-6 lg:px-4 xl:px-6 2xl:px-8 py-6 md:py-6 lg:py-8 xl:py-16 2xl:py-20 shadow-md md:shadow-xl shadow-black rounded-md 2xl:rounded-xl border border-[#363636]"
          style={{
            background:
              "linear-gradient(to bottom, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.2))",
          }}
        >

          <h2 className="text-xl xl:text-2xl 2xl:text-3xl font-semibold 2xl:font-bold text-center">
            Create Account
          </h2>

          <p className="mt-2 lg:mt-4 text-sm xl:text-md 2xl:text-lg text-center text-gray-200">
            Already have an account?{" "}
            <span
              className="text-white underline cursor-pointer hover:font-semibold duration-300"
              onClick={onSwitchMode}
              disabled={loading}
            >
              Login
            </span>
          </p>

          <form
            className="mt-4 lg:mt-5 xl:mt-6 2xl:mt-8"
            onSubmit={submitHandler}
          >
            <div className="mt-2">
              <label
                htmlFor="text"
                className="font-semibold text-sm lg:text-[15px] xl:text-md 2xl:text-lg"
              >
                User Name
              </label>
              <input
                placeholder="username"
                className="w-full text-black p-2  rounded border-none outline-none h-[40px] md:h-[45px] xl:h-[50px] 2xl:h-[55px] xl:text-md 2xl:text-lg placeholder:text-md placeholder:font-normal"
                id=""
                type="text"
                required
                value={email}
                onChange={(e) =>setEmail(e.target.value.trim())}
                disabled={loading}
              />
            </div>

            <div className="mt-2">
              <label
                htmlFor="password"
                className="font-semibold text-sm lg:text-[15px] xl:text-md 2xl:text-lg"
              >
                Password
              </label>
                {/* Password Input with Toggle Button */}
                <div className="relative mt-1">
                  <input
                    id="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    placeholder="Enter your password"
                    type={showPassword ? "text" : "password"}
                    className="w-full rounded p-2 pr-12 h-[40px] md:h-[45px] xl:h-[50px] 2xl:h-[55px] 
                              border border-gray-300 outline-none text-black
                              xl:text-md 2xl:text-lg placeholder:text-md"
                  />

                  {/* Toggle Button */}
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 transition"
                  >
                    {showPassword ? (
                      <Eye className="h-5 w-5" />
                    ) : (
                      <EyeOff className="h-5 w-5" />
                    )}
                  </button>
                </div>
            </div>

            <div className="mt-2">
              <label
                htmlFor="confirm-password"
                className="font-semibold text-sm lg:text-[15px] xl:text-md 2xl:text-lg"
              >
                Confirm Password
              </label>
                {/* Password Input with Toggle Button */}
                <div className="relative mt-1">
                  <input
                    id="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                    placeholder="Enter your password"
                    type={showConfirmPassword ? "text" : "password"}
                    className="w-full rounded p-2 pr-12 h-[40px] md:h-[45px] xl:h-[50px] 2xl:h-[55px] 
                              border border-gray-300 outline-none text-black
                              xl:text-md 2xl:text-lg placeholder:text-md"
                  />

                  {/* Toggle Button */}
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 transition"
                  >
                    {showConfirmPassword ? (
                      <Eye className="h-5 w-5" />
                    ) : (
                      <EyeOff className="h-5 w-5" />
                    )}
                  </button>
                </div>
            </div>

            {error && <p className="text-red-500 font-medium my-2">{error}</p>}

            <div className="mt-4">
              <button
                className="w-full h-[40px] md:h-[45px] xl:h-[50px] 2xl:h-[55px] rounded-md bg-[#363636] border-none outline-none font-medium duration-300"
                type="submit"
                disabled={loading}
              >
                {loading ? "Signing Up..." : "Sign Up"}
              </button>
            </div>
          </form>

          <div className="flex justify-between items-center gap-2 mt-2 xl:mt-4 hidden sm:flex">
            <button
              className="flex items-center justify-center w-1/2 h-[40px] 2xl:h-[50px] bg-white text-black font-medium rounded border-none outline-none"
              type="button"
              disabled={loading}
              onClick={googleSignUpHandler}
            >
              <span className="mr-2 inline-block">
                <svg
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-rose-500"
                >
                  <path d="M20.283 10.356h-8.327v3.451h4.792c-.446 2.193-2.313 3.453-4.792 3.453a5.27 5.27 0 0 1-5.279-5.28 5.27 5.27 0 0 1 5.279-5.279c1.259 0 2.397.447 3.29 1.178l2.6-2.599c-1.584-1.381-3.615-2.233-5.89-2.233a8.908 8.908 0 0 0-8.934 8.934 8.907 8.907 0 0 0 8.934 8.934c4.467 0 8.529-3.249 8.529-8.934 0-.528-.081-1.097-.202-1.625z"></path>
                </svg>
              </span>
              {loading ? "Signing Up..." : "Sign Up"}
            </button>
            <button
              className="flex items-center justify-center w-1/2 h-[40px] 2xl:h-[50px] bg-white text-black font-medium rounded border-none outline-none"
              type="button"
              disabled={loading}
              onClick={facebookSignUpHandler}
            >
              <span className="mr-2 inline-block">
                <svg
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 rounded-full text-blue-500"
                >
                  <path d="M22.675 0h-21.35C.594 0 0 .594 0 1.325v21.351C0 23.407.594 24 1.325 24H12.82v-9.294H9.692V11.29h3.128V8.413c0-3.1 1.893-4.788 4.657-4.788 1.325 0 2.462.099 2.792.143v3.24l-1.918.001c-1.504 0-1.796.715-1.796 1.763v2.31h3.587l-.467 3.416h-3.12V24h6.116C23.407 24 24 23.407 24 22.676V1.325C24 .594 23.407 0 22.675 0z" />
                </svg>
              </span>
              {loading ? "Signing Up..." : "Sign Up"}
            </button>
          </div>
        </div>
      </div>

      <div className="md:h-[60px] xl:h-[80px] 2xl:h-[100px]">
        <h1 className="text-lg xl:text-xl 2xl:text-2xl font-semibold">
          Welcome
        </h1>
        <blockquote className="text-sm lg:w-1/2 xl:text-md 2xl:text-xl font-medium">
          <q>
            From sensors to satellites, tech irrigation brings precision to the
            fields, ensuring sustainable agriculture
          </q>
        </blockquote>
      </div>
    </div>
  );
};

export default SignupComponent;